import * as THREE from 'three'
import { AttachmentIntegrityRuntime, type AttachmentSlot } from './AttachmentIntegrity'
import { classifyMeshPart, type MeshPartClassification, type MeshPartDescriptor } from './MeshPartOwnership'
import { applyMeshPartOwnership } from './meshPartOwnershipRuntime'

type ManagedPart={
  node:THREE.Object3D
  mount:THREE.Group
  parent:THREE.Object3D
  integrity:AttachmentIntegrityRuntime
  lastWorldPosition:THREE.Vector3
  lastWorldVelocity:THREE.Vector3
  initialized:boolean
}

function materialNames(mesh:THREE.Mesh):string[]{
 const list=Array.isArray(mesh.material)?mesh.material:[mesh.material]
 return list.map(m=>m?.name||m?.type||'material')
}

function classificationFor(node:THREE.Object3D):MeshPartClassification{
 const existingKind=node.userData.__meshPartKind
 const existingPhysics=node.userData.__meshPartPhysics
 if(existingKind&&existingPhysics){
  return{
   kind:existingKind,
   physics:existingPhysics,
   confidence:Number(node.userData.__meshPartConfidence??1),
   signals:[],
   safeForIndependentTransform:Boolean(node.userData.__meshPartIndependent),
   requiresDerivedSplit:Boolean(node.userData.__meshPartRequiresDerivedSplit),
  }
 }
 const mesh=node as THREE.Mesh
 const skinned=Boolean((mesh as THREE.SkinnedMesh).isSkinnedMesh)
 const descriptor:MeshPartDescriptor={
  nodeName:node.name||'unnamed-mesh',
  materialNames:materialNames(mesh),
  skinned,
  boneCount:skinned?((mesh as THREE.SkinnedMesh).skeleton?.bones?.length??0):0,
  morphTargetCount:Object.keys(mesh.morphTargetDictionary??{}).length,
  vertexCount:mesh.geometry?.attributes?.position?.count??0,
 }
 return classifyMeshPart(descriptor)
}

function slotFor(name:string,classification:MeshPartClassification):AttachmentSlot{
 const n=name.toLowerCase()
 if(/cape|cloak/.test(n))return 'cape'
 if(/hair|braid|ponytail/.test(n))return 'hair'
 if(/wing.*left|left.*wing/.test(n))return 'wingLeft'
 if(/wing.*right|right.*wing/.test(n))return 'wingRight'
 if(/tail/.test(n))return 'tail'
 if(/shield/.test(n))return 'shield'
 if(/weapon|sword|blade|axe|hammer|staff|bow|gun|rifle|lance|spear/.test(n))return /left|_l\b|\.l\b/.test(n)?'weaponLeft':'weaponRight'
 if(/helmet|helm/.test(n))return 'helmet'
 if(/mask/.test(n))return 'mask'
 if(/necklace|amulet/.test(n))return 'necklace'
 if(classification.kind==='garment')return 'cape'
 if(classification.kind==='hair')return 'hair'
 if(classification.kind==='rigid-carried')return 'weaponRight'
 return 'backAccessory'
}

/**
 * Fight adapter for the shared MeshPartOwnership + AttachmentIntegrity engines.
 *
 * Rules:
 * - Skinned meshes never leave their skeleton at runtime.
 * - Only explicitly independent non-skinned parts may be wrapped/reparented.
 * - Monolithic body+garment assets are reported for an offline derived split.
 * - Every managed part owns a separate mount and separate physics runtime.
 */
export class FighterAttachmentIntegrityRuntime{
 private readonly parts:ManagedPart[]=[]
 private readonly worldPos=new THREE.Vector3()
 private readonly velocity=new THREE.Vector3()
 private readonly acceleration=new THREE.Vector3()
 private readonly parentQuat=new THREE.Quaternion()

 readonly stats={
  body:0,
  skinnedGarments:0,
  secondaryGarments:0,
  armor:0,
  rigidSockets:0,
  accessories:0,
  monolithicMixed:0,
  derivedSplitRequired:0,
  unknown:0,
  managed:0,
 }

 constructor(private readonly root:THREE.Object3D,private readonly identity:string){
  // Canonical semantic/material ownership runs first. The physics adapter
  // consumes those exact classifications and never invents a second policy.
  applyMeshPartOwnership(root,identity)
  const candidates:Array<{node:THREE.Object3D;classification:MeshPartClassification}>=[]
  root.traverse(node=>{
   if(!(node as THREE.Mesh).isMesh)return
   const classification=classificationFor(node)
   node.userData.__runtimeDetachForbidden=!classification.safeForIndependentTransform
   node.userData.__attachmentPhysicsAuthority=classification.physics

   if(classification.requiresDerivedSplit)this.stats.derivedSplitRequired++
   if(classification.kind==='body')this.stats.body++
   else if(classification.kind==='garment'||classification.kind==='hair'){
    if((node as THREE.SkinnedMesh).isSkinnedMesh)this.stats.skinnedGarments++
    else this.stats.secondaryGarments++
   }else if(classification.kind==='armor')this.stats.armor++
   else if(classification.kind==='rigid-carried')this.stats.rigidSockets++
   else if(classification.kind==='accessory')this.stats.accessories++
   else if(classification.kind==='monolithic-mixed')this.stats.monolithicMixed++
   else if(classification.kind==='unknown')this.stats.unknown++

   if(
    classification.safeForIndependentTransform&&
    (classification.physics==='secondary-garment'||classification.physics==='rigid-socket')
   ){
    candidates.push({node,classification})
   }
  })

  for(const {node,classification} of candidates){
   const parent=node.parent
   if(!parent)continue
   const slot=slotFor(node.name||classification.kind,classification)
   const mount=new THREE.Group()
   mount.name=`fight-attachment-mount:${identity}:${slot}:${node.name||node.uuid}`
   mount.position.set(0,0,0)
   mount.rotation.set(0,0,0)
   mount.scale.set(1,1,1)
   parent.add(mount)
   mount.add(node)
   node.userData.__attachmentIntegrityManaged=true
   node.userData.__attachmentIntegritySlot=slot

   const integrity=new AttachmentIntegrityRuntime(
    `fight:${identity}:${node.uuid}`,
    slot,
    classification.physics==='rigid-socket'?{mode:'rigid-socket'}:{mode:'secondary-garment'},
   )
   this.parts.push({
    node,mount,parent,integrity,
    lastWorldPosition:new THREE.Vector3(),
    lastWorldVelocity:new THREE.Vector3(),
    initialized:false,
   })
  }
  this.stats.managed=this.parts.length
  root.userData.__attachmentIntegrity={
   ...this.stats,
   policy:'shared-mesh-ownership; never-detach-skinned; derived-split-monolithic',
  }
 }

 update(rawDt:number,frameBlend01=1):void{
  const dt=THREE.MathUtils.clamp(Number.isFinite(rawDt)?rawDt:0,0,1/15)
  if(!(dt>0))return
  for(const part of this.parts){
   part.parent.getWorldPosition(this.worldPos)
   if(!part.initialized){
    part.lastWorldPosition.copy(this.worldPos)
    part.lastWorldVelocity.set(0,0,0)
    part.initialized=true
   }
   this.velocity.copy(this.worldPos).sub(part.lastWorldPosition).multiplyScalar(1/dt)
   this.acceleration.copy(this.velocity).sub(part.lastWorldVelocity).multiplyScalar(1/dt)
   part.parent.getWorldQuaternion(this.parentQuat)
   this.acceleration.applyQuaternion(this.parentQuat.invert())

   const frame=part.integrity.step({
    dt,
    localAcceleration:{x:this.acceleration.x,y:this.acceleration.y,z:this.acceleration.z},
    contact01:1,
    frameBlend01:THREE.MathUtils.clamp(frameBlend01,0,1),
   })
   part.mount.position.set(frame.offset.x,frame.offset.y,frame.offset.z)
   part.mount.rotation.set(frame.rotation.x,frame.rotation.y,frame.rotation.z,'XYZ')
   part.lastWorldPosition.copy(this.worldPos)
   part.lastWorldVelocity.copy(this.velocity)
  }
 }

 dispose():void{
  for(const part of this.parts){
   part.integrity.reset()
   const parent=part.mount.parent
   if(parent){
    part.mount.remove(part.node)
    parent.add(part.node)
    parent.remove(part.mount)
   }
   delete part.node.userData.__attachmentIntegrityManaged
   delete part.node.userData.__attachmentIntegritySlot
  }
  this.parts.length=0
  delete this.root.userData.__attachmentIntegrity
 }
}
